import { HealthRecord, AverageMetrics } from "../components/HealthMetricsModal";
import { config } from "../config/environment";

const API_BASE_URL = config.apiUrl;

console.log("[healthMetricsService] Initialized with API_BASE_URL:", API_BASE_URL);

interface BloodPressureData {
  measurement_time: string;
  systolic: number;
  diastolic: number;
  pulse?: number | null;
  measurement_position: string;
  body_position: string;
  device_type: string;
  notes?: string;
  data_source: string;
}

interface BloodGlucoseData {
  measurement_time: string;
  glucose_level: number;
  meal_relation: string;
  specimen_source: string;
  device_type: string;
  notes?: string;
  data_source: string;
}

const getAuthToken = (): string => {
  // AuthContext에서 저장한 토큰 키 확인
  let token = localStorage.getItem("authToken");
  
  // 호환성을 위해 'token'도 확인
  if (!token) {
    token = localStorage.getItem("token");
  }
  
  if (!token) {
    console.warn("[healthMetricsService] No auth token found in localStorage");
    throw new Error("인증 토큰이 없습니다. 다시 로그인해주세요.");
  }
  
  return token;
};

// 혈압 기록 추가
export const addBloodPressure = async (data: BloodPressureData): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/health/blood-pressure`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "혈압 기록 추가 실패");
  }

  return response.json();
};

// 혈당 기록 추가
export const addBloodGlucose = async (data: BloodGlucoseData): Promise<any> => {
  const response = await fetch(`${API_BASE_URL}/health/blood-glucose`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getAuthToken()}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || "혈당 기록 추가 실패");
  }

  return response.json();
};

// 최근 혈압 기록 조회
export const getLatestBloodPressure = async (): Promise<HealthRecord | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-pressure/latest?limit=1&offset=0`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getLatestBloodPressure] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈압 기록 조회 실패");
    }

    const result = await response.json();
    console.log("[getLatestBloodPressure] Response:", result.data);
    // 배열이 반환되므로 첫 번째 요소 반환
    return Array.isArray(result.data) ? result.data[0] || null : result.data;
  } catch (error) {
    console.error("[getLatestBloodPressure] Error:", error);
    throw error;
  }
};

// 최근 혈당 기록 조회
export const getLatestBloodGlucose = async (): Promise<HealthRecord | null> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-glucose/latest?limit=1&offset=0`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getLatestBloodGlucose] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈당 기록 조회 실패");
    }

    const result = await response.json();
    console.log("[getLatestBloodGlucose] Response:", result.data);
    // 배열이 반환되므로 첫 번째 요소 반환
    return Array.isArray(result.data) ? result.data[0] || null : result.data;
  } catch (error) {
    console.error("[getLatestBloodGlucose] Error:", error);
    throw error;
  }
};

// 혈압 평균값 조회
export const getBloodPressureAverage = async (): Promise<AverageMetrics> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-pressure/average`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getBloodPressureAverage] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈압 평균값 조회 실패");
    }

    const result = await response.json();
    console.log("[getBloodPressureAverage] Response:", result);
    return result.data || {};
  } catch (error) {
    console.error("[getBloodPressureAverage] Error:", error);
    throw error;
  }
};

// 혈당 평균값 조회
export const getBloodGlucoseAverage = async (): Promise<AverageMetrics> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-glucose/average`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getBloodGlucoseAverage] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈당 평균값 조회 실패");
    }

    const result = await response.json();
    console.log("[getBloodGlucoseAverage] Response:", result);
    return result.data || {};
  } catch (error) {
    console.error("[getBloodGlucoseAverage] Error:", error);
    throw error;
  }
};

// 최근 혈압 기록 여러 개 조회 (최대 10개)
export const getBloodPressureHistory = async (limit: number = 10): Promise<HealthRecord[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-pressure/latest?limit=${limit}`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getBloodPressureHistory] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈압 기록 조회 실패");
    }

    const result = await response.json();
    return Array.isArray(result.data) ? result.data : [];
  } catch (error) {
    console.error("[getBloodPressureHistory] Error:", error);
    return [];
  }
};

// 최근 혈당 기록 여러 개 조회 (최대 10개)
export const getBloodGlucoseHistory = async (limit: number = 10): Promise<HealthRecord[]> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-glucose/latest?limit=${limit}`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getBloodGlucoseHistory] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈당 기록 조회 실패");
    }

    const result = await response.json();
    return Array.isArray(result.data) ? result.data : [];
  } catch (error) {
    console.error("[getBloodGlucoseHistory] Error:", error);
    return [];
  }
};

// 혈압 기록 페이징 조회
export const getBloodPressureHistoryPaginated = async (limit: number = 20, offset: number = 0): Promise<{ data: HealthRecord[]; hasMore: boolean }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-pressure/latest?limit=${limit}&offset=${offset}`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getBloodPressureHistoryPaginated] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈압 기록 조회 실패");
    }

    const result = await response.json();
    return {
      data: Array.isArray(result.data) ? result.data : [],
      hasMore: result.hasMore || false,
    };
  } catch (error) {
    console.error("[getBloodPressureHistoryPaginated] Error:", error);
    throw error;
  }
};

// 혈당 기록 페이징 조회
export const getBloodGlucoseHistoryPaginated = async (limit: number = 20, offset: number = 0): Promise<{ data: HealthRecord[]; hasMore: boolean }> => {
  try {
    const response = await fetch(`${API_BASE_URL}/health/blood-glucose/latest?limit=${limit}&offset=${offset}`, {
      headers: {
        Authorization: `Bearer ${getAuthToken()}`,
      },
    });

    if (!response.ok) {
      if (response.status === 401) {
        console.warn("[getBloodGlucoseHistoryPaginated] Unauthorized - token may be expired");
        localStorage.removeItem("authToken");
        localStorage.removeItem("token");
        throw new Error("인증이 만료되었습니다. 다시 로그인해주세요.");
      }
      const error = await response.json();
      throw new Error(error.error || "혈당 기록 조회 실패");
    }

    const result = await response.json();
    return {
      data: Array.isArray(result.data) ? result.data : [],
      hasMore: result.hasMore || false,
    };
  } catch (error) {
    console.error("[getBloodGlucoseHistoryPaginated] Error:", error);
    throw error;
  }
};
